import { useScienceTutorSection } from './scienceTutorContext';
import { useState } from 'react';

import type { Element } from './polymersBiomolecules';
import {
  evaluateHydrolysis,
  FOOD_SAMPLES,
  foodEnergy,
  foodEnergyQuestion,
  HYDROLYSED_BOND_OPTIONS,
  HYDROLYSIS_CASES,
  hydrolysisEquation,
  hydrolysisProductCounts,
  hydrolysisReactantCounts,
  hydrolysisWordEquation,
  roundKilojoules,
  WATER_COUNT_OPTIONS,
  type FoodSample,
  type HydrolysedBond,
  type HydrolysisCase,
  type HydrolysisResult,
  type Nutrient,
} from './polymersBiomoleculesDigestion';
import { ChoicePanel, FeedbackBox } from './PolymersShared';
import { CARD_CLASS, chipClass } from './polymersStyles';

const BALANCE_ELEMENTS: readonly Element[] = ['C', 'H', 'N', 'O', 'S'];

const NUTRIENT_LABEL: Record<Nutrient, string> = {
  protein: 'Protein',
  carbohydrate: 'Carbohydrate',
  fat: 'Fat',
};

/** Each bar colour is repeated beside a named row in the working table, so colour is never the only cue. */
const NUTRIENT_BAR_CLASS: Record<Nutrient, string> = {
  protein: 'bg-brand-sky',
  carbohydrate: 'bg-brand-sunshine',
  fat: 'bg-brand-coral',
};

function HydrolysisBalance({ item }: { item: HydrolysisCase }) {
  const left = hydrolysisReactantCounts(item);
  const right = hydrolysisProductCounts(item);
  const elements = BALANCE_ELEMENTS.filter(
    (element) => (left[element] ?? 0) > 0 || (right[element] ?? 0) > 0,
  );
  return (
    <table className="mt-3 w-full text-left text-sm" aria-label="Hydrolysis atom balance">
      <thead>
        <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
          <th className="py-1">Element</th>
          <th className="py-1">Reactants</th>
          <th className="py-1">Products</th>
        </tr>
      </thead>
      <tbody>
        {elements.map((element) => {
          const reactants = left[element] ?? 0;
          const products = right[element] ?? 0;
          return (
            <tr key={element} className="border-t border-hairline">
              <td className="py-1.5 font-bold text-ink">{element}</td>
              <td className="py-1.5 font-mono text-ink">{reactants}</td>
              <td className="py-1.5 font-mono text-ink">
                {products} {reactants === products ? '✓' : '✗'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function HydrolysisCaseView({ item }: { item: HydrolysisCase }) {
  const [bond, setBond] = useState<HydrolysedBond | null>(null);
  const [water, setWater] = useState<number | null>(null);
  useScienceTutorSection('HydrolysisCaseView', { bond, water });
  const [result, setResult] = useState<HydrolysisResult | null>(null);

  const choose = (next: () => void) => {
    next();
    setResult(null);
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="grid gap-5 lg:grid-cols-2">
        <fieldset>
          <legend className="text-sm font-extrabold text-ink">Which link breaks?</legend>
          <div className="mt-3 grid gap-2">
            {HYDROLYSED_BOND_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={bond === option.id}
                onClick={() => choose(() => setBond(option.id))}
                className={chipClass(bond === option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="text-sm font-extrabold text-ink">
            How many H₂O are needed for complete hydrolysis?
          </legend>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {WATER_COUNT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={water === option}
                aria-label={`${option} H₂O used`}
                onClick={() => choose(() => setWater(option))}
                className={`${chipClass(water === option)} font-mono`}
              >
                {option} H₂O
              </button>
            ))}
          </div>
        </fieldset>
      </div>
      <button
        type="button"
        disabled={bond === null || water === null}
        onClick={() => {
          if (bond === null || water === null) return;
          setResult(evaluateHydrolysis(item.id, { bond, water }));
        }}
        className="btn-pill-primary disabled:cursor-not-allowed disabled:opacity-40"
      >
        Hydrolyse
      </button>
      {result?.status === 'unsupported' ? (
        <FeedbackBox correct={false}>{result.message}</FeedbackBox>
      ) : null}
      {result?.status === 'checked' ? (
        <>
          <div className="grid gap-3 lg:grid-cols-2">
            <FeedbackBox
              correct={result.bondCorrect}
              title={result.bondCorrect ? 'Link: correct' : 'Link: not yet'}
            >
              {result.bondFeedback}
            </FeedbackBox>
            <FeedbackBox
              correct={result.waterCorrect}
              title={result.waterCorrect ? 'Water: correct' : 'Water: not yet'}
            >
              {result.waterFeedback}
            </FeedbackBox>
          </div>
          <section className="rounded-[1.75rem] border border-brand-mint/30 bg-wash-mint p-5">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">
              Model revealed · balanced hydrolysis
            </p>
            <p className="mt-2 font-bold text-ink">{hydrolysisWordEquation(item)}</p>
            <p className="mt-2 break-words rounded-2xl bg-white/80 p-3 font-mono font-extrabold text-ink">
              {hydrolysisEquation(item)}
            </p>
            <HydrolysisBalance item={item} />
            <p className="mt-3 text-sm font-medium leading-6 text-slate2">{item.note}</p>
          </section>
        </>
      ) : null}
    </div>
  );
}

export function PolymersDigestionPanel() {
  const [caseId, setCaseId] = useState(HYDROLYSIS_CASES[0].id);
  useScienceTutorSection('PolymersDigestionPanel', { caseId });
  const item = HYDROLYSIS_CASES.find((entry) => entry.id === caseId) ?? HYDROLYSIS_CASES[0];
  return (
    <section className={CARD_CLASS} aria-labelledby="digestion-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
        3 · Digestion is hydrolysis
      </p>
      <h3 id="digestion-heading" className="mt-1 text-xl font-extrabold text-ink">
        Break the links with water
      </h3>
      <div
        className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
        role="group"
        aria-label="Choose a food molecule"
      >
        {HYDROLYSIS_CASES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === item.id}
            onClick={() => setCaseId(entry.id)}
            className={chipClass(entry.id === item.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <p className="mt-3 text-sm font-medium leading-6 text-slate2">
        Enzyme: <span className="font-extrabold text-ink">{item.enzyme}</span>. Predict the link it
        breaks and the water it uses.
      </p>
      <HydrolysisCaseView key={item.id} item={item} />
    </section>
  );
}

function EnergyBars({ food }: { food: FoodSample }) {
  const energy = foodEnergy(food.grams)!;
  const total = energy.totalKilojoules;
  return (
    <div className="mt-3">
      <div
        className="flex h-6 w-full overflow-hidden rounded-full bg-surface"
        role="img"
        aria-label={`Energy split for ${food.name}: ${energy.contributions
          .map((item) => `${NUTRIENT_LABEL[item.nutrient]} ${roundKilojoules(item.kilojoules)} kJ`)
          .join(', ')}; total ${roundKilojoules(total)} kJ.`}
      >
        {energy.contributions
          .filter((item) => item.kilojoules > 0)
          .map((item) => (
            <div
              key={item.nutrient}
              className={NUTRIENT_BAR_CLASS[item.nutrient]}
              style={{ width: `${(item.kilojoules / total) * 100}%` }}
            />
          ))}
      </div>
      <table className="mt-3 w-full text-left text-sm" aria-label="Energy working">
        <thead>
          <tr className="text-xs font-extrabold uppercase tracking-[0.1em] text-slate2">
            <th className="py-1">Nutrient</th>
            <th className="py-1">Mass × factor</th>
            <th className="py-1">Energy</th>
          </tr>
        </thead>
        <tbody>
          {energy.contributions.map((item) => (
            <tr key={item.nutrient} className="border-t border-hairline">
              <td className="py-1.5 font-bold text-ink">
                <span
                  className={`mr-2 inline-block h-3 w-3 rounded-full ${NUTRIENT_BAR_CLASS[item.nutrient]}`}
                  aria-hidden="true"
                />
                {NUTRIENT_LABEL[item.nutrient]}
              </td>
              <td className="py-1.5 font-mono text-ink">
                {item.grams} g × {item.factor} kJ g⁻¹
              </td>
              <td className="py-1.5 font-mono text-ink">{roundKilojoules(item.kilojoules)} kJ</td>
            </tr>
          ))}
          <tr className="border-t-2 border-ink">
            <td className="py-1.5 font-extrabold text-ink" colSpan={2}>
              Total
            </td>
            <td className="py-1.5 font-mono font-extrabold text-ink">
              {roundKilojoules(total)} kJ
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function FoodEnergyCase({ food }: { food: FoodSample }) {
  const question = foodEnergyQuestion(food.id)!;
  const [revealed, setRevealed] = useState(false);
  useScienceTutorSection('FoodEnergyCase', { revealed });
  return (
    <div className="mt-4 grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <ChoicePanel question={question} onChecked={() => setRevealed(true)} />
      </div>
      <div className="min-w-0 rounded-[1.5rem] border border-brand-sky/20 bg-wash-sky p-4">
        {revealed ? (
          <>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">
              Model revealed · where the energy comes from
            </p>
            <EnergyBars food={food} />
          </>
        ) : (
          <p className="text-sm font-bold leading-6 text-slate2">
            Check an answer first. The energy split by nutrient appears after your prediction.
          </p>
        )}
      </div>
    </div>
  );
}

export function PolymersFoodEnergyPanel() {
  const [foodId, setFoodId] = useState(FOOD_SAMPLES[0].id);
  useScienceTutorSection('PolymersFoodEnergyPanel', { foodId });
  const food = FOOD_SAMPLES.find((entry) => entry.id === foodId) ?? FOOD_SAMPLES[0];
  return (
    <section className={CARD_CLASS} aria-labelledby="food-energy-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">
        4 · Food energy
      </p>
      <h3 id="food-energy-heading" className="mt-1 text-xl font-extrabold text-ink">
        Energy from protein, carbohydrate and fat
      </h3>
      <p className="mt-2 text-sm font-medium leading-6 text-slate2">
        Use protein 17 kJ g⁻¹, carbohydrate 16 kJ g⁻¹ and fat 37 kJ g⁻¹, the standard values in
        Australian senior chemistry. Australian food labels use the FSANZ energy factors: protein and
        fat are the same, and the label factor for carbohydrate can differ slightly, so a printed
        total may not match exactly. Compositions here are rounded illustrative values.
      </p>
      <div
        className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4"
        role="group"
        aria-label="Choose a food"
      >
        {FOOD_SAMPLES.map((entry) => (
          <button
            key={entry.id}
            type="button"
            aria-pressed={entry.id === food.id}
            onClick={() => setFoodId(entry.id)}
            className={chipClass(entry.id === food.id)}
          >
            {entry.name}
          </button>
        ))}
      </div>
      <FoodEnergyCase key={food.id} food={food} />
    </section>
  );
}
